// src/hooks/useTokenManagement.ts
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  PaymentDetails,
  PaymentResponse,
  TokenPackage,
  TokenService,
  Transaction,
  UserToken
} from '../services/token.service';
import { PaymentStatus } from '../types/payment.types';

export interface UpdateStatusModalState {
  open: boolean;
  transaction: Transaction | null;
  status: PaymentStatus | '';
}

export function useTokenManagement() {
  const [packages, setPackages] = useState<TokenPackage[]>([]);
  const [userToken, setUserToken] = useState<UserToken | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [paymentData, setPaymentData] = useState<PaymentResponse | null>(null);
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [pagination, setPagination] = useState<{
    total: number;
    page: number;
    limit: number;
    pages: number;
  }>({ total: 0, page: 1, limit: 10, pages: 0 });

  // Tab management
  const [activeTab, setActiveTab] = useState<string | null>('packages');
  
  // Modal states
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [updateStatusModal, setUpdateStatusModal] = useState<UpdateStatusModalState>({
    open: false,
    transaction: null,
    status: ''
  });
  
  // Payment check interval
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

  const [searchParams] = useSearchParams();
  const { tokenType } = useParams<{ tokenType?: string }>();
  
  const orderCode = searchParams.get('orderCode');
  const status = searchParams.get('status');

  // Lấy danh sách gói token
  const fetchTokenPackages = async () => {
    try {
      setIsLoading(true);
      const data = await TokenService.getTokenPackages();
      setPackages(data);
    } catch (err) {
      setError('Failed to fetch token packages');
      console.error('Error fetching token packages:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Lấy thông tin token hiện tại của user
  const fetchUserToken = async () => {
    try {
      setIsLoading(true);
      const data = await TokenService.getUserToken();
      setUserToken(data);
    } catch (err) {
      console.error('Error fetching user token:', err);
      // Không set error ở đây vì có thể gây ra nhiều thông báo lỗi
    } finally {
      setIsLoading(false);
    }
  };

  // Lấy lịch sử giao dịch
  const fetchTransactions = async (page: number = 1, limit: number = 10) => {
    try {
      setIsLoading(true);
      const data = await TokenService.getTransactions(page, limit);
      setTransactions(data.transactions);
      setPagination(data.pagination);
    } catch (err) {
      setError('Failed to fetch transactions');
      console.error('Error fetching transactions:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Tạo thanh toán
  const createPayment = async (packageId: string) => {
    try {
      setIsLoading(true);
      setError('');
      const data = await TokenService.createPayment(packageId);
      setPaymentData(data);
      return data;
    } catch (err) {
      setError('Failed to create payment');
      console.error('Error creating payment:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Kiểm tra trạng thái thanh toán
  const checkPaymentStatus = async (paymentId: string) => {
    try {
      setIsLoading(true);
      const data = await TokenService.getPaymentDetails(paymentId);
      setPaymentDetails(data);
      return data;
    } catch (err) {
      console.error('Error checking payment status:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Mở trang thanh toán
  const openPaymentPage = (paymentUrl: string) => {
    window.open(paymentUrl, '_blank');
  };

  // Sử dụng token
  const useTokens = async (tokenAmount: number, text: string, characterName: string) => {
    try {
      setIsLoading(true);
      const data = await TokenService.useTokens(tokenAmount, text, characterName);
      setUserToken(data);
      return true;
    } catch (err: any) {
      if (err?.message?.includes('Insufficient token balance')) {
        return { error: 'insufficient_tokens', requiredTokens: tokenAmount };
      }
      setError('Failed to use tokens');
      console.error('Error using tokens:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Tính số token còn lại và ngày hết hạn theo định dạng đẹp
  const formatTokenInfo = () => {
    if (!userToken) return { formattedBalance: '0', formattedExpiry: 'N/A' };
    
    const formattedBalance = userToken.balance.toLocaleString();
    
    let formattedExpiry = 'Never';
    if (userToken.expiryDate) {
      const expiryDate = new Date(userToken.expiryDate);
      formattedExpiry = expiryDate.toLocaleDateString('vi-VN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    
    return { formattedBalance, formattedExpiry };
  };

  // Format giá tiền
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(price);
  };

  // Kiểm tra token còn đủ không
  const hasEnoughTokens = (textLength: number) => {
    if (!userToken) return false;
    return userToken.balance >= textLength;
  };

  // Cập nhật trạng thái thanh toán thủ công
  const updatePaymentStatus = async (paymentId: string, status: PaymentStatus) => {
    try {
      setIsLoading(true);
      setError('');
      await TokenService.updatePaymentStatus(paymentId, status);
      return true;
    } catch (err) {
      setError('Failed to update payment status');
      console.error('Error updating payment status:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Xử lý khi click mua gói
  const handleBuyPackage = async (packageId: string) => {
    const response = await createPayment(packageId);
    if (response) {
      setPaymentModalOpen(true);
      
      // Polling để kiểm tra trạng thái thanh toán mỗi 10s
      const interval = setInterval(async () => {
        const details = await checkPaymentStatus(response.orderCode);
        if (details && (details.status === PaymentStatus.COMPLETED || details.status === PaymentStatus.PAID)) {
          clearInterval(interval);
          setPaymentModalOpen(false);
          setSuccessModalOpen(true);
          fetchUserToken();
          if (activeTab === 'transactions') {
            fetchTransactions(1);
          }
        }
      }, 10000);
      
      setPollingInterval(interval);
    }
  };
  
  // Mở modal cập nhật trạng thái
  const openUpdateStatusModal = (transaction: Transaction) => {
    setUpdateStatusModal({
      open: true,
      transaction,
      status: transaction.status
    });
  };
  
  // Xử lý cập nhật trạng thái
  const handleUpdateStatus = async () => {
    if (!updateStatusModal.transaction) return;
    
    const success = await updatePaymentStatus(
      updateStatusModal.transaction.paymentId,
      updateStatusModal.status as PaymentStatus
    );
    
    if (success) {
      setUpdateStatusModal({ open: false, transaction: null, status: '' });
      fetchTransactions(pagination.page);
      fetchUserToken();
    }
  };
  
  // Hiển thị màu theo trạng thái
  const getStatusColor = (status: PaymentStatus) => {
    switch (status) {
      case PaymentStatus.COMPLETED:
      case PaymentStatus.PAID:
        return 'green';
      case PaymentStatus.PENDING:
        return 'yellow';
      case PaymentStatus.FAILED:
      case PaymentStatus.CANCELLED:
      case PaymentStatus.EXPIRED:
        return 'red';
      default:
        return 'gray';
    }
  };

  // Định dạng trạng thái
  const formatStatus = (status: PaymentStatus) => {
    switch (status) {
      case PaymentStatus.COMPLETED:
      case PaymentStatus.PAID:
        return 'Đã thanh toán';
      case PaymentStatus.PENDING:
        return 'Đang chờ';
      case PaymentStatus.FAILED:
        return 'Thất bại';
      case PaymentStatus.CANCELLED:
        return 'Đã hủy';
      case PaymentStatus.EXPIRED:
        return 'Hết hạn';
      default:
        return status;
    }
  };
  
  // Lọc gói theo loại (tháng/năm)
  const getFilteredPackages = () => {
    return packages.filter(pkg => {
      if (tokenType === 'monthly') return pkg.isMonthly;
      if (tokenType === 'yearly') return !pkg.isMonthly;
      return true;
    });
  };
  
  // Đóng modal và clear interval
  const closePaymentModal = () => {
    if (pollingInterval) clearInterval(pollingInterval);
    setPaymentModalOpen(false);
  };
  
  // Đóng modal update status
  const closeUpdateStatusModal = () => {
    setUpdateStatusModal({ open: false, transaction: null, status: '' });
  };
  
  // Cập nhật trạng thái cho modal
  const updateStatusModalState = (status: PaymentStatus | '') => {
    setUpdateStatusModal({
      ...updateStatusModal,
      status: status || ''
    });
  };

  // Load data khi component mount
  useEffect(() => {
    fetchTokenPackages();
    fetchUserToken();
  }, []);

  // Kiểm tra query params để thực hiện các hành động
  useEffect(() => {
    if (orderCode && status === 'success') {
      (async () => {
        const details = await checkPaymentStatus(orderCode);
        if (details && (details.status === PaymentStatus.COMPLETED || details.status === PaymentStatus.PAID)) {
          setSuccessModalOpen(true);
          fetchUserToken();
          if (activeTab === 'transactions') {
            fetchTransactions(1);
          }
        }
      })();
    }
  }, [orderCode, status]);
  
  // Load transactions khi chọn tab transactions
  useEffect(() => {
    if (activeTab === 'transactions') {
      fetchTransactions(1);
    }
  }, [activeTab]);

  // Clear interval khi đóng modal hoặc unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  return {
    // State data
    packages,
    userToken,
    isLoading,
    error,
    paymentData,
    paymentDetails,
    transactions,
    pagination,
    activeTab,
    paymentModalOpen,
    successModalOpen,
    updateStatusModal,
    
    // Derived data
    filteredPackages: getFilteredPackages(),
    
    // Formatting functions
    formatTokenInfo,
    formatPrice,
    getStatusColor,
    formatStatus,
    
    // Core token operations
    hasEnoughTokens,
    createPayment,
    checkPaymentStatus,
    openPaymentPage,
    useTokens,
    fetchTransactions,
    refreshUserToken: fetchUserToken,
    clearError: () => setError(''),
    updatePaymentStatus,
    
    // UI handlers
    setActiveTab,
    handleBuyPackage,
    openUpdateStatusModal,
    handleUpdateStatus,
    closePaymentModal,
    closeUpdateStatusModal,
    updateStatusModalState,
    setSuccessModalOpen: (open: boolean) => setSuccessModalOpen(open)
  };
}